/**
 * PATH: packages/ui/src/components/settings/appearance-tab/ThemeSelector.tsx
 * PURPOSE: Theme mode selector with Light/Dark/System option cards
 * WHY: Extracted from AppearanceTab to keep orchestrator under 300 lines
 *
 * FLOW:
 * ┌──────────────┐    ┌─────────────────┐    ┌──────────────────┐
 * │ currentTheme │ →  │ 3 option cards   │ →  │ onThemeChange()  │
 * └──────────────┘    └─────────────────┘    └──────────────────┘
 *
 * DEPENDENCIES:
 *  - lucide-react: Sun/Moon/Monitor icons
 *  - cn utility: conditional class merging
 */

import { Sun, Moon, Monitor, Check } from 'lucide-react'
import { cn } from '../../../lib/utils'

// ─────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────

type ThemeMode = 'light' | 'dark' | 'system'

interface ThemeSelectorProps {
  currentTheme: ThemeMode
  onThemeChange: (theme: ThemeMode) => void
}

// ─────────────────────────────────────────────────────────────
// ThemeOption (private helper)
// ─────────────────────────────────────────────────────────────

interface ThemeOptionProps {
  icon: React.ReactNode
  label: string
  description: string
  isSelected: boolean
  onSelect: () => void
}

function ThemeOption({ icon, label, description, isSelected, onSelect }: ThemeOptionProps) {
  return (
    <button
      onClick={onSelect}
      className={cn(
        'flex items-start gap-4 p-4 rounded-xl border-2 transition-all text-left w-full',
        isSelected
          ? 'border-teal-500 bg-teal-500/5 dark:bg-teal-500/10'
          : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 bg-white dark:bg-slate-800'
      )}
    >
      <div className={cn(
        'w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0',
        isSelected
          ? 'bg-teal-500 text-white'
          : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400'
      )}>
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-slate-900 dark:text-white">{label}</span>
          {isSelected && (
            <Check className="w-4 h-4 text-teal-500" />
          )}
        </div>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
          {description}
        </p>
      </div>
    </button>
  )
}

// ─────────────────────────────────────────────────────────────
// ThemeSelector
// ─────────────────────────────────────────────────────────────

export function ThemeSelector({ currentTheme, onThemeChange }: ThemeSelectorProps) {
  return (
    <div>
      <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-4">
        Theme
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <ThemeOption
          icon={<Sun className="w-5 h-5" />}
          label="Light"
          description="Bright and clear"
          isSelected={currentTheme === 'light'}
          onSelect={() => onThemeChange('light')}
        />
        <ThemeOption
          icon={<Moon className="w-5 h-5" />}
          label="Dark"
          description="Easy on the eyes"
          isSelected={currentTheme === 'dark'}
          onSelect={() => onThemeChange('dark')}
        />
        <ThemeOption
          icon={<Monitor className="w-5 h-5" />}
          label="System"
          description="Match device setting"
          isSelected={currentTheme === 'system'}
          onSelect={() => onThemeChange('system')}
        />
      </div>
    </div>
  )
}
